import { useTranslation } from 'react-i18next';
import { Text } from '@/shared/ui';
import Badge from '@/shared/ui/components/basic/Badge';
import type { Condition, VistoryRoom } from '../types/vistory.types';

const CONDITION_COLORS: Record<Condition, { color: string; bg: string }> = {
  novo: { color: 'var(--status-success)', bg: 'var(--status-success-subtle)' },
  bom: { color: 'var(--status-success)', bg: 'var(--status-success-subtle)' },
  regular: { color: 'var(--status-warning)', bg: 'var(--status-warning-subtle)' },
  ruim: { color: 'var(--status-error)', bg: 'var(--status-error-subtle)' },
  nao_aplicavel: { color: 'var(--text-tertiary)', bg: 'var(--surface-tertiary)' },
};

interface Props {
  rooms: VistoryRoom[];
}

/**
 * Read-only display of structured vistory rooms with their items and conditions.
 *
 * @param {Props} props - The rooms array from a structured vistory.
 * @returns {JSX.Element} Rendered room cards.
 */
export function VistoryRoomView({ rooms }: Props) {
  const { t } = useTranslation('features/vistories');
  const sorted = [...rooms].sort((a, b) => a.order - b.order);

  return (
    <div className="flex flex-col gap-[var(--space-4)]">
      <Text size="sm" fw={600} className="text-[var(--text-primary)]">
        {t('vistory.rooms_title')} ({rooms.length})
      </Text>
      {sorted.map((room) => (
        <RoomCard key={room.id} room={room} />
      ))}
    </div>
  );
}

/**
 * Renders a room card with its inspection items and photos.
 */
function RoomCard({ room }: { room: VistoryRoom }) {
  const { t } = useTranslation('features/vistories');
  return (
    <div className="rounded-(--radius-lg) border border-[var(--border-subtle)] bg-[var(--surface-primary)] p-[var(--space-3)] shadow-[var(--shadow-sm)]">
      <div className="flex items-center justify-between mb-[var(--space-2)]">
        <Text size="sm" fw={500} className="text-[var(--text-primary)]">{room.name}</Text>
        <Text span size="xs" className="text-[var(--text-tertiary)]">
          {room.photos.length} {t('vistory.photos_count')}
        </Text>
      </div>
      {room.items.length > 0 && (
        <div className="flex flex-col gap-[var(--space-2)]">
          {room.items.map((item) => (
            <ItemRow key={item.id} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Renders a single inspection item row with its condition tone.
 */
function ItemRow({ item }: { item: VistoryRoom['items'][number] }) {
  const { t } = useTranslation('features/vistories');
  const tone = CONDITION_COLORS[item.condition] ?? CONDITION_COLORS.nao_aplicavel;
  const attrEntries = Object.entries(item.attributes);

  return (
    <div className="rounded-(--radius-md) border border-[var(--border-subtle)] bg-[var(--surface-secondary)] p-[var(--space-2)]">
      <div className="flex items-center justify-between gap-[var(--space-2)]">
        <Text span size="xs" fw={500} className="text-[var(--text-primary)]">{item.label}</Text>
        <Badge color={tone.color} backgroundColor={tone.bg} size="sm">
          {t(`vistory.condition_${item.condition}`)}
        </Badge>
      </div>
      {attrEntries.length > 0 && (
        <div className="mt-[var(--space-1)] flex flex-wrap gap-[var(--space-2)]">
          {attrEntries.map(([key, val]) => (
            <Text key={key} span size="xs" className="text-[var(--text-tertiary)]">
              {key}: {val}
            </Text>
          ))}
        </div>
      )}
      {item.observation && (
        <Text size="xs" className="mt-[var(--space-1)] text-[var(--text-secondary)] italic">
          {item.observation}
        </Text>
      )}
      {item.photos.length > 0 && (
        <Text span size="xs" className="mt-[var(--space-1)] text-[var(--text-tertiary)]">
          {item.photos.length} {t('vistory.photos_count')}
        </Text>
      )}
    </div>
  );
}
